import { useState } from 'react';
import { Plus, Tag as TagIcon, X } from 'lucide-react';
import { Button, Modal, Field, inputClass } from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import type { LinkedDesigner, Designer } from './storeTypes';

export function StoreDesigners({
  storeId,
  designers,
  onChanged,
}: {
  storeId: string;
  designers: LinkedDesigner[];
  onChanged: () => void;
}) {
  const { mutate, loading: saving } = useMutation();
  const { data: designerData } = useApi<{ designers: Designer[] }>('/designers');
  const allDesigners = designerData?.designers ?? [];

  const [open, setOpen] = useState(false);
  const [selectId, setSelectId] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const linkedIds = new Set(designers.map((d) => d.id));
  const available = allDesigners.filter((d) => !linkedIds.has(d.id));

  const close = () => {
    setOpen(false);
    setSelectId('');
    setNewName('');
  };

  const handleLink = async () => {
    const name = newName.trim();
    if (!selectId && !name) return;
    const body = selectId ? { designerId: selectId } : { designerName: name };
    const res = await mutate('post', `/stores/${storeId}/designers`, body);
    if (res !== null) {
      close();
      onChanged();
    }
  };

  const handleUnlink = async (designerId: string) => {
    // The shared api.delete sends no body, but this endpoint needs one — use raw fetch.
    setBusy(true);
    try {
      const res = await fetch(`/api/stores/${storeId}/designers`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ designerId }),
      });
      if (res.ok) onChanged();
    } catch {
      // ignore — UI stays as-is
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Designers</h4>
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
        >
          <Plus className="w-3.5 h-3.5" />
          Link Designer
        </button>
      </div>

      {designers.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-2">No designers linked</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {designers.map((d) => (
            <span
              key={d.id}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground"
            >
              <TagIcon className="w-3 h-3" />
              {d.name}
              <button
                onClick={() => handleUnlink(d.id)}
                disabled={busy}
                className="p-0.5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50"
                aria-label={`Unlink ${d.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Modal
        isOpen={open}
        onClose={close}
        title="Link Designer"
        footer={
          <Button fullWidth onClick={handleLink} disabled={(!selectId && !newName.trim()) || saving}>
            {saving ? 'Linking…' : 'Link Designer'}
          </Button>
        }
      >
        <div className="space-y-4">
          {available.length > 0 && (
            <Field label="Existing designer">
              <select
                value={selectId}
                onChange={(e) => {
                  setSelectId(e.target.value);
                  if (e.target.value) setNewName('');
                }}
                className={inputClass}
              >
                <option value="">— Select a designer —</option>
                {available.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Or new designer name" hint="Type a name to create and link a new designer.">
            <input
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                if (e.target.value) setSelectId('');
              }}
              placeholder="e.g., The Row"
              className={inputClass}
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
